import { z } from 'zod';
import { PRODUCT_STATUSES } from './catalog';
import { UUID_PATTERN, uuidSchema } from './common';
import { ALLOWED_IMAGE_CONTENT_TYPES, IMAGE_CONTENT_TYPE_EXTENSIONS } from './image-asset';
import { publicProductSummarySchema } from './products-public';

/**
 * 랜딩 페이지 섹션과 S3 세그먼트 `landing/{세그먼트}/{uuid}.{확장자}`.
 * 키 생성(api landing)과 키 형식 검사가 같은 표를 본다 — 섹션을 늘릴 때는 여기서만 늘린다.
 */
export const LANDING_SECTION_SEGMENTS = {
  HERO: 'hero',
  PORTFOLIO: 'portfolio',
  TESTIMONIAL: 'testimonials',
  PARTNER: 'partners',
} as const;

/** 히어로 배경 사진 최대 장수. 사용자 확정값이 아닌 기본 제안값이다. */
export const MAX_LANDING_HERO_IMAGES = 10;

/** 키 형식 전체를 고정해 `.`·`/` 로 다른 프리픽스에 닿는 경로를 막는다(catalog ASSET_KEY_PATTERN 과 같은 방식). */
const landingImageKeyPatternOf = (segment: string): RegExp =>
  new RegExp(
    `^landing/${segment}/${UUID_PATTERN}\\.(${Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|')})$`,
  );

export const LANDING_HERO_IMAGE_KEY_PATTERN = landingImageKeyPatternOf(
  LANDING_SECTION_SEGMENTS.HERO,
);
export const LANDING_PORTFOLIO_IMAGE_KEY_PATTERN = landingImageKeyPatternOf(
  LANDING_SECTION_SEGMENTS.PORTFOLIO,
);

export const landingHeroImageKeySchema = z
  .string()
  .regex(LANDING_HERO_IMAGE_KEY_PATTERN, '랜딩 히어로 이미지 키 형식이 아닙니다.');

export const presignLandingHeroUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignLandingHeroUploadInput = z.infer<typeof presignLandingHeroUploadInputSchema>;

export const presignLandingHeroUploadResultSchema = z.strictObject({
  uploadUrl: z.url(),
  /** 업로드가 끝나면 등록·교체 요청에 그대로 되돌려준다. */
  key: landingHeroImageKeySchema,
  expiresInSeconds: z.number().int().positive(),
});
export type PresignLandingHeroUploadResult = z.infer<typeof presignLandingHeroUploadResultSchema>;

export const createLandingHeroImageInputSchema = z.strictObject({
  imageKey: landingHeroImageKeySchema,
});
export type CreateLandingHeroImageInput = z.infer<typeof createLandingHeroImageInputSchema>;

export const replaceLandingHeroImageInputSchema = z.strictObject({
  id: uuidSchema,
  imageKey: landingHeroImageKeySchema,
});
export type ReplaceLandingHeroImageInput = z.infer<typeof replaceLandingHeroImageInputSchema>;

export const LANDING_MOVE_DIRECTIONS = ['UP', 'DOWN'] as const;
export const moveLandingHeroImageInputSchema = z.strictObject({
  id: uuidSchema,
  direction: z.enum(LANDING_MOVE_DIRECTIONS),
});
export type MoveLandingHeroImageInput = z.infer<typeof moveLandingHeroImageInputSchema>;

export const deleteLandingHeroImageInputSchema = z.strictObject({ id: uuidSchema });
export type DeleteLandingHeroImageInput = z.infer<typeof deleteLandingHeroImageInputSchema>;

/** 배너에 상품을 연결하거나(productId) 연결을 끊는다(null). 공개 상품만 연결할 수 있다. */
export const setLandingHeroImageProductInputSchema = z.strictObject({
  id: uuidSchema,
  productId: uuidSchema.nullable(),
});
export type SetLandingHeroImageProductInput = z.infer<typeof setLandingHeroImageProductInputSchema>;

/**
 * 배너 문구(직접 입력) 필드별 최대 글자 수. landing_images CHECK 와 같은 값이다.
 * 비운 필드는 web 이 연결 상품 정보로 대신한다.
 */
export const LANDING_HERO_COPY_MAX_LENGTHS = {
  eyebrow: 30,
  title: 60,
  subtitle: 120,
  ctaLabel: 20,
  ctaHref: 300,
} as const;

/**
 * 버튼 링크 — 사이트 안 경로("/store") 또는 https 주소만 받는다.
 * "//host"(프로토콜 상대)·"/\host"·javascript: 등은 다른 사이트나 스크립트로 이어지므로 막는다.
 */
export const LANDING_HERO_CTA_HREF_PATTERN = /^(\/([^/\\]|$)|https:\/\/)/;

const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname.length > 0;
  } catch {
    return false;
  }
};

/** 공백만 있으면 비운 것(null)으로 본다 — "비우면 상품 정보" 규칙이 공백 문자열에 막히지 않게. */
const copyTextSchema = (label: string, max: number) =>
  z
    .string()
    .trim()
    .max(max, `${label}은(는) ${max}자 이하여야 합니다.`)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));

export const landingHeroCtaHrefSchema = z
  .string()
  .trim()
  .max(
    LANDING_HERO_COPY_MAX_LENGTHS.ctaHref,
    `버튼 링크는 ${LANDING_HERO_COPY_MAX_LENGTHS.ctaHref}자 이하여야 합니다.`,
  )
  .refine(
    (value) =>
      value === '' ||
      (LANDING_HERO_CTA_HREF_PATTERN.test(value) && (value.startsWith('/') || isHttpsUrl(value))),
    '버튼 링크는 "/"로 시작하는 사이트 경로나 https:// 주소여야 합니다.',
  );

const nullableCtaHrefSchema = landingHeroCtaHrefSchema
  .nullable()
  .transform((value) => (value === null || value === '' ? null : value));

const landingHeroCopyFieldsShape = {
  eyebrow: copyTextSchema('작은 라벨', LANDING_HERO_COPY_MAX_LENGTHS.eyebrow),
  title: copyTextSchema('큰 문구', LANDING_HERO_COPY_MAX_LENGTHS.title),
  subtitle: copyTextSchema('보조 문구', LANDING_HERO_COPY_MAX_LENGTHS.subtitle),
  ctaLabel: copyTextSchema('버튼 문구', LANDING_HERO_COPY_MAX_LENGTHS.ctaLabel),
  ctaHref: nullableCtaHrefSchema,
};

/** 버튼 문구와 링크는 함께 있거나 함께 비어 있다(DB CHECK 와 같은 규칙). */
const refineCtaPair = (
  value: { readonly ctaLabel: string | null; readonly ctaHref: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (value.ctaLabel !== null && value.ctaHref === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['ctaHref'],
      message: '버튼 문구를 넣었다면 버튼 링크도 입력해 주세요.',
    });
  }
  if (value.ctaLabel === null && value.ctaHref !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['ctaLabel'],
      message: '버튼 링크를 넣었다면 버튼 문구도 입력해 주세요.',
    });
  }
};

/** 배너 문구 입력 필드 — admin 폼이 서버와 같은 규칙으로 검사한다. 빈 문자열·공백은 null 이 된다. */
export const landingHeroCopyFieldsSchema = z
  .strictObject(landingHeroCopyFieldsShape)
  .superRefine(refineCtaPair);
export type LandingHeroCopyFieldsInput = z.input<typeof landingHeroCopyFieldsSchema>;

/** 배너 문구를 통째로 저장한다. 모든 필드가 null 이면 문구를 지운다(연결 상품 정보만 남는다). */
export const setLandingHeroImageCopyInputSchema = z
  .strictObject({ id: uuidSchema, ...landingHeroCopyFieldsShape })
  .superRefine(refineCtaPair);
export type SetLandingHeroImageCopyInput = z.output<typeof setLandingHeroImageCopyInputSchema>;

/** 저장된 배너 문구(응답). 비운 필드는 null. */
export const landingHeroCopySchema = z.object({
  eyebrow: z.string().nullable(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  ctaLabel: z.string().nullable(),
  ctaHref: z.string().nullable(),
});
export type LandingHeroCopy = z.infer<typeof landingHeroCopySchema>;

/**
 * 어드민 목록의 연결 상품 — 연결 뒤 숨김·삭제된 상품도 그대로 보여 준다.
 * 운영자가 상태를 보고 다른 상품으로 바꾸게 한다(공개 목록에서는 빠진다).
 */
export const landingHeroLinkedProductSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(PRODUCT_STATUSES),
  isDeleted: z.boolean(),
});
export type LandingHeroLinkedProduct = z.infer<typeof landingHeroLinkedProductSchema>;

/** 어드민 목록 항목 — 키와 순서, 연결 상품, 배너 문구를 함께 준다. */
export const landingHeroImageAdminSchema = z.object({
  id: z.uuid(),
  imageKey: landingHeroImageKeySchema,
  imageUrl: z.url(),
  displayOrder: z.number().int(),
  updatedAt: z.iso.datetime(),
  product: landingHeroLinkedProductSchema.nullable(),
  copy: landingHeroCopySchema,
});
export type LandingHeroImageAdmin = z.infer<typeof landingHeroImageAdminSchema>;
export const landingHeroImageAdminListSchema = z.array(landingHeroImageAdminSchema);

/**
 * 공개 목록 항목 — 키는 싣지 않는다. 배열 순서가 노출 순서다.
 * product 는 연결 상품이 공개(PUBLISHED)·미삭제일 때만 싣고, 아니면 null.
 * copy 는 직접 입력한 문구 — 필드마다 copy 가 먼저, 비었으면 product 정보로 대신한다(둘 다 없으면 사진만).
 */
export const landingHeroImagePublicSchema = z.object({
  id: z.uuid(),
  imageUrl: z.url(),
  product: publicProductSummarySchema.nullable(),
  copy: landingHeroCopySchema,
});
export type LandingHeroImagePublic = z.infer<typeof landingHeroImagePublicSchema>;
export const landingHeroImagePublicListSchema = z.array(landingHeroImagePublicSchema);

// ---------------------------------------------------------------------------
// 포트폴리오(portfolio_items) — SUPER-60. 사진은 landing/portfolio/{uuid}.{ext}.
// ---------------------------------------------------------------------------

/** 포트폴리오 최대 개수(SUPER-60 확정). */
export const MAX_PORTFOLIO_ITEMS = 8;
export const PORTFOLIO_TITLE_MAX_LENGTH = 100;
export const PORTFOLIO_CLIENT_NAME_MAX_LENGTH = 100;
export const PORTFOLIO_DESCRIPTION_MAX_LENGTH = 500;

export const landingPortfolioImageKeySchema = z
  .string()
  .regex(LANDING_PORTFOLIO_IMAGE_KEY_PATTERN, '포트폴리오 이미지 키 형식이 아닙니다.');

/**
 * 글 필드 — admin 폼과 서버가 같은 스키마로 검사한다.
 * 필드를 늘릴 때는 여기와 portfolio_items 컬럼을 함께 늘린다.
 */
export const portfolioItemFieldsSchema = z.strictObject({
  title: z
    .string()
    .trim()
    .min(1, '제목을 입력해 주세요.')
    .max(PORTFOLIO_TITLE_MAX_LENGTH, `제목은 ${PORTFOLIO_TITLE_MAX_LENGTH}자 이하여야 합니다.`),
  clientName: z
    .string()
    .trim()
    .min(1, '고객사명을 입력해 주세요.')
    .max(
      PORTFOLIO_CLIENT_NAME_MAX_LENGTH,
      `고객사명은 ${PORTFOLIO_CLIENT_NAME_MAX_LENGTH}자 이하여야 합니다.`,
    ),
  description: z
    .string()
    .trim()
    .min(1, '설명을 입력해 주세요.')
    .max(
      PORTFOLIO_DESCRIPTION_MAX_LENGTH,
      `설명은 ${PORTFOLIO_DESCRIPTION_MAX_LENGTH}자 이하여야 합니다.`,
    ),
});
export type PortfolioItemFields = z.infer<typeof portfolioItemFieldsSchema>;

export const presignLandingPortfolioUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignLandingPortfolioUploadInput = z.infer<
  typeof presignLandingPortfolioUploadInputSchema
>;

export const presignLandingPortfolioUploadResultSchema = z.strictObject({
  uploadUrl: z.url(),
  /** 업로드가 끝나면 등록·교체 요청에 그대로 되돌려준다. */
  key: landingPortfolioImageKeySchema,
  expiresInSeconds: z.number().int().positive(),
});
export type PresignLandingPortfolioUploadResult = z.infer<
  typeof presignLandingPortfolioUploadResultSchema
>;

export const createPortfolioItemInputSchema = z.strictObject({
  ...portfolioItemFieldsSchema.shape,
  imageKey: landingPortfolioImageKeySchema,
});
export type CreatePortfolioItemInput = z.infer<typeof createPortfolioItemInputSchema>;

export const updatePortfolioItemInputSchema = z.strictObject({
  id: uuidSchema,
  ...portfolioItemFieldsSchema.shape,
});
export type UpdatePortfolioItemInput = z.infer<typeof updatePortfolioItemInputSchema>;

export const replacePortfolioImageInputSchema = z.strictObject({
  id: uuidSchema,
  imageKey: landingPortfolioImageKeySchema,
});
export type ReplacePortfolioImageInput = z.infer<typeof replacePortfolioImageInputSchema>;

export const movePortfolioItemInputSchema = z.strictObject({
  id: uuidSchema,
  direction: z.enum(LANDING_MOVE_DIRECTIONS),
});
export type MovePortfolioItemInput = z.infer<typeof movePortfolioItemInputSchema>;

export const deletePortfolioItemInputSchema = z.strictObject({ id: uuidSchema });
export type DeletePortfolioItemInput = z.infer<typeof deletePortfolioItemInputSchema>;

/** 어드민 목록 항목 — 키와 순서를 함께 준다. 배열 순서가 노출 순서다. */
export const portfolioItemAdminSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  clientName: z.string(),
  description: z.string(),
  imageKey: landingPortfolioImageKeySchema,
  imageUrl: z.url(),
  displayOrder: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type PortfolioItemAdmin = z.infer<typeof portfolioItemAdminSchema>;
export const portfolioItemAdminListSchema = z.array(portfolioItemAdminSchema);

/** 공개 목록 항목 — 키는 싣지 않는다. 배열 순서가 노출 순서다. */
export const portfolioItemPublicSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  clientName: z.string(),
  description: z.string(),
  imageUrl: z.url(),
});
export type PortfolioItemPublic = z.infer<typeof portfolioItemPublicSchema>;
export const portfolioItemPublicListSchema = z.array(portfolioItemPublicSchema);

// ---------------------------------------------------------------------------
// HOME 기업소개 — 기업 고객 사례(landing_testimonials, 사진·제목·본문·담당자)와 함께한 기업 로고(landing_partners).
// 운영자가 직접 등록하는 카드·로고다. 상품 후기(reviews)·포트폴리오 사례와 별개이며,
// 사례 카드는 등록된 것만 보이고(없으면 구역 숨김), 기업 로고는 비어 있으면 포트폴리오 사례 로고를 쓴다.
// 사진은 landing/testimonials/{uuid}.{ext} · landing/partners/{uuid}.{ext}.
// ---------------------------------------------------------------------------

/** 기업 고객 사례 카드 최대 수 — 데스크톱 네 장 두 줄. */
export const MAX_LANDING_TESTIMONIALS = 8;
export const LANDING_TESTIMONIAL_MAX_LENGTHS = { title: 60, body: 300, author: 40 } as const;

export const LANDING_TESTIMONIAL_IMAGE_KEY_PATTERN = landingImageKeyPatternOf(
  LANDING_SECTION_SEGMENTS.TESTIMONIAL,
);
export const landingTestimonialImageKeySchema = z
  .string()
  .regex(LANDING_TESTIMONIAL_IMAGE_KEY_PATTERN, '기업 고객 사례 사진 키 형식이 아닙니다.');

/** 글 필드 — admin 폼과 서버가 같은 스키마로 검사한다. 필드를 늘릴 때는 landing_testimonials 컬럼도 함께 늘린다. */
export const landingTestimonialFieldsSchema = z.strictObject({
  title: z
    .string()
    .trim()
    .min(1, '제목을 입력해 주세요.')
    .max(
      LANDING_TESTIMONIAL_MAX_LENGTHS.title,
      `제목은 ${LANDING_TESTIMONIAL_MAX_LENGTHS.title}자 이하여야 합니다.`,
    ),
  body: z
    .string()
    .trim()
    .min(1, '본문을 입력해 주세요.')
    .max(
      LANDING_TESTIMONIAL_MAX_LENGTHS.body,
      `본문은 ${LANDING_TESTIMONIAL_MAX_LENGTHS.body}자 이하여야 합니다.`,
    ),
  author: z
    .string()
    .trim()
    .min(1, '담당자를 입력해 주세요.')
    .max(
      LANDING_TESTIMONIAL_MAX_LENGTHS.author,
      `담당자는 ${LANDING_TESTIMONIAL_MAX_LENGTHS.author}자 이하여야 합니다.`,
    ),
});
export type LandingTestimonialFields = z.infer<typeof landingTestimonialFieldsSchema>;

export const presignLandingTestimonialUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignLandingTestimonialUploadInput = z.infer<
  typeof presignLandingTestimonialUploadInputSchema
>;
export const presignLandingTestimonialUploadResultSchema = z.strictObject({
  uploadUrl: z.url(),
  key: landingTestimonialImageKeySchema,
  expiresInSeconds: z.number().int().positive(),
});
export type PresignLandingTestimonialUploadResult = z.infer<
  typeof presignLandingTestimonialUploadResultSchema
>;

export const createLandingTestimonialInputSchema = z.strictObject({
  ...landingTestimonialFieldsSchema.shape,
  imageKey: landingTestimonialImageKeySchema,
});
export type CreateLandingTestimonialInput = z.infer<typeof createLandingTestimonialInputSchema>;
export const updateLandingTestimonialInputSchema = z.strictObject({
  id: uuidSchema,
  ...landingTestimonialFieldsSchema.shape,
});
export type UpdateLandingTestimonialInput = z.infer<typeof updateLandingTestimonialInputSchema>;
export const replaceLandingTestimonialImageInputSchema = z.strictObject({
  id: uuidSchema,
  imageKey: landingTestimonialImageKeySchema,
});
export type ReplaceLandingTestimonialImageInput = z.infer<
  typeof replaceLandingTestimonialImageInputSchema
>;
export const moveLandingTestimonialInputSchema = z.strictObject({
  id: uuidSchema,
  direction: z.enum(LANDING_MOVE_DIRECTIONS),
});
export type MoveLandingTestimonialInput = z.infer<typeof moveLandingTestimonialInputSchema>;
export const deleteLandingTestimonialInputSchema = z.strictObject({ id: uuidSchema });
/** 끌어다 놓기 결과 — 전체 id 를 새 순서대로. 서버가 현재 목록과 같은 집합인지 확인하고 0부터 다시 매긴다. */
export const reorderLandingTestimonialsInputSchema = z.strictObject({
  orderedIds: z.array(uuidSchema).min(1),
});
export type ReorderLandingTestimonialsInput = z.infer<typeof reorderLandingTestimonialsInputSchema>;
export type DeleteLandingTestimonialInput = z.infer<typeof deleteLandingTestimonialInputSchema>;

/** 어드민 목록 항목 — 키와 순서를 함께 준다. 배열 순서가 노출 순서다. */
export const landingTestimonialAdminSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  author: z.string(),
  imageKey: landingTestimonialImageKeySchema,
  imageUrl: z.url(),
  displayOrder: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type LandingTestimonialAdmin = z.infer<typeof landingTestimonialAdminSchema>;
export const landingTestimonialAdminListSchema = z.array(landingTestimonialAdminSchema);

/** 공개 목록 항목 — 키는 싣지 않는다. 배열 순서가 노출 순서다. */
export const landingTestimonialPublicSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  author: z.string(),
  imageUrl: z.url(),
});
export type LandingTestimonialPublic = z.infer<typeof landingTestimonialPublicSchema>;
export const landingTestimonialPublicListSchema = z.array(landingTestimonialPublicSchema);

/** 함께한 기업 로고 최대 수 — 데스크톱 여섯 칸 네 줄. */
export const MAX_LANDING_PARTNERS = 24;
export const LANDING_PARTNER_NAME_MAX_LENGTH = 60;

export const LANDING_PARTNER_IMAGE_KEY_PATTERN = landingImageKeyPatternOf(
  LANDING_SECTION_SEGMENTS.PARTNER,
);
export const landingPartnerImageKeySchema = z
  .string()
  .regex(LANDING_PARTNER_IMAGE_KEY_PATTERN, '기업 로고 키 형식이 아닙니다.');

/** 기업명 — 로고의 대체 텍스트. admin 폼과 서버가 같은 스키마로 검사한다. */
export const landingPartnerFieldsSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .min(1, '기업명을 입력해 주세요.')
    .max(
      LANDING_PARTNER_NAME_MAX_LENGTH,
      `기업명은 ${LANDING_PARTNER_NAME_MAX_LENGTH}자 이하여야 합니다.`,
    ),
});
export type LandingPartnerFields = z.infer<typeof landingPartnerFieldsSchema>;

export const presignLandingPartnerUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignLandingPartnerUploadInput = z.infer<
  typeof presignLandingPartnerUploadInputSchema
>;
export const presignLandingPartnerUploadResultSchema = z.strictObject({
  uploadUrl: z.url(),
  key: landingPartnerImageKeySchema,
  expiresInSeconds: z.number().int().positive(),
});
export type PresignLandingPartnerUploadResult = z.infer<
  typeof presignLandingPartnerUploadResultSchema
>;

export const createLandingPartnerInputSchema = z.strictObject({
  ...landingPartnerFieldsSchema.shape,
  imageKey: landingPartnerImageKeySchema,
});
export type CreateLandingPartnerInput = z.infer<typeof createLandingPartnerInputSchema>;
export const updateLandingPartnerInputSchema = z.strictObject({
  id: uuidSchema,
  ...landingPartnerFieldsSchema.shape,
});
export type UpdateLandingPartnerInput = z.infer<typeof updateLandingPartnerInputSchema>;
export const replaceLandingPartnerImageInputSchema = z.strictObject({
  id: uuidSchema,
  imageKey: landingPartnerImageKeySchema,
});
export type ReplaceLandingPartnerImageInput = z.infer<typeof replaceLandingPartnerImageInputSchema>;
export const moveLandingPartnerInputSchema = z.strictObject({
  id: uuidSchema,
  direction: z.enum(LANDING_MOVE_DIRECTIONS),
});
export type MoveLandingPartnerInput = z.infer<typeof moveLandingPartnerInputSchema>;
export const deleteLandingPartnerInputSchema = z.strictObject({ id: uuidSchema });
/** 끌어다 놓기 결과 — 전체 id 를 새 순서대로. 서버가 현재 목록과 같은 집합인지 확인하고 0부터 다시 매긴다. */
export const reorderLandingPartnersInputSchema = z.strictObject({
  orderedIds: z.array(uuidSchema).min(1),
});
export type ReorderLandingPartnersInput = z.infer<typeof reorderLandingPartnersInputSchema>;
export type DeleteLandingPartnerInput = z.infer<typeof deleteLandingPartnerInputSchema>;

/** 어드민 목록 항목 — 키와 순서를 함께 준다. 배열 순서가 노출 순서다. */
export const landingPartnerAdminSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  imageKey: landingPartnerImageKeySchema,
  imageUrl: z.url(),
  displayOrder: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type LandingPartnerAdmin = z.infer<typeof landingPartnerAdminSchema>;
export const landingPartnerAdminListSchema = z.array(landingPartnerAdminSchema);

/** 공개 목록 항목 — 키는 싣지 않는다. 배열 순서가 노출 순서다. */
export const landingPartnerPublicSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  imageUrl: z.url(),
});
export type LandingPartnerPublic = z.infer<typeof landingPartnerPublicSchema>;
export const landingPartnerPublicListSchema = z.array(landingPartnerPublicSchema);
